import { Fragment, useContext, useEffect, useState } from "react";
import { formatCurrency } from "../../util/format-text";
import { isoDateToString } from "../../util/format-date";
import { MdCheck, MdClose, MdEdit, MdRefresh } from "react-icons/md";
import DetailPair from "../UI/DetailPair";
import Heading from "../UI/Heading";
import { UtilityContext } from "../../context/util-context";
import { requestAdminAPI } from "../../api/posh-api";

/** ========================================================================
 * * Application Market Value
 * ========================================================================== */

export default function MarketValue({ application, refresh }) {
  const { setConfirm } = useContext(UtilityContext);

  // Initialize States
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(false);

  /* ======================= UPDATE MARKET VALUE ======================= */
  const updateMarketValue = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("update-market-value", {
        applicationId: application.id,
      });
      refresh();
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  /* ======================= EDIT HANDLER ======================= */
  const editHandler = async () => {
    try {
      setLoading(true);
      await requestAdminAPI("update-market-value", {
        applicationId: application.id,
        manual: true,
        high: document.getElementById("high").value,
        average: document.getElementById("average").value,
        low: document.getElementById("low").value,
        mileage: document.getElementById("mileage").value,
      });
      refresh();
      setEdit(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (application) setLoading(false);
    else setLoading(true);
  }, [application]);

  // Check if value needs update (if last check was more than a month ago)
  let needsUpdate = false;
  if (!application.lastValueCheck) needsUpdate = true;
  else {
    const lastChecked = new Date(application.lastValueCheck);
    // One month after last check
    lastChecked.setMonth(lastChecked.getMonth() + 1);
    if (lastChecked < new Date()) needsUpdate = true;
  }
  return (
    <div className="grid gap-4">
      <Heading
        isBackArrow={false}
        title="Market Value"
        text="Following shows the estimated value of the vehicle based on market data from past 90 days."
      >
        {edit ? (
          <Fragment>
            <MdCheck onClick={editHandler} className="icon" />
            <MdClose onClick={() => setEdit(false)} className="icon" />
          </Fragment>
        ) : (
          <Fragment>
            <MdEdit onClick={() => setEdit(true)} className="icon" />
            <MdRefresh
              onClick={() =>
                setConfirm({
                  message:
                    "POSH is charged every time market value is updated. Please avoid updating unless last updated is red.",
                  action: updateMarketValue,
                })
              }
              className={`icon ${loading && "animate-spin"}`}
            />
          </Fragment>
        )}
      </Heading>
      <div className="grid-cols-3 info">
        <DetailPair
          label="High"
          value={formatCurrency(application?.highValue)}
          classes="text-green-600"
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "high",
            type: "number",
            step: "0.01",
            defaultValue: application?.highValue,
          }}
        />
        <DetailPair
          label="Average"
          value={formatCurrency(application?.avgValue)}
          classes="text-xl font-semibold text-yellow-600"
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "average",
            type: "number",
            step: "0.01",
            defaultValue: application?.avgValue,
          }}
        />
        <DetailPair
          label="Low"
          value={formatCurrency(application?.lowValue)}
          classes="text-blue-500"
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "low",
            type: "number",
            step: "0.01",
            defaultValue: application?.lowValue,
          }}
        />
        <DetailPair
          label="Adjusted Mileage"
          value={
            application?.lastValueMileage && `${application.lastValueMileage}mi`
          }
          loading={loading}
          edit={edit}
          inputOptions={{
            id: "mileage",
            type: "number",
            step: "1",
            defaultValue: application?.lastValueMileage,
          }}
        />
        <DetailPair
          label="Certainty"
          value={
            application?.valueCertainty == 0
              ? "Manually Added"
              : application?.valueCertainty
                ? `${application.valueCertainty}%`
                : "-"
          }
          loading={loading}
        />
        <DetailPair
          label="Last Updated"
          value={isoDateToString(application?.lastValueCheck)}
          classes={
            needsUpdate ? "text-red-600 animate-pulse" : "text-green-600"
          }
          loading={loading}
        />
      </div>
    </div>
  );
}
